import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spin, Typography, Space, Statistic } from 'antd';
import { SyncOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { getOdooConfig, getContactsCount, syncKpiWithOdoo } from '../../api/odoo-crm';

const { Text, Title } = Typography;

const OdooSyncModal = ({ visible, onClose, kpiId, kpiName, token, onSyncSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [contactsCount, setContactsCount] = useState(null);
  const [isOdooActive, setIsOdooActive] = useState(false);
  const [configExists, setConfigExists] = useState(false);

  useEffect(() => {
    if (visible) {
      checkOdooStatus();
    }
  }, [visible]);

  const checkOdooStatus = async () => {
    setLoading(true);
    try {
      const configResponse = await getOdooConfig(token);
      if (configResponse.data) {
        setConfigExists(true);
        setIsOdooActive(configResponse.data.isActive);
        
        if (configResponse.data.isActive) {
          // Fetch contacts count to show preview
          try {
            const countResponse = await getContactsCount(token);
            setContactsCount(countResponse.data.count);
          } catch (error) {
            console.error('Failed to fetch contacts count:', error);
          }
        }
      } else {
        setConfigExists(false);
        setIsOdooActive(false);
      }
    } catch (error) {
      console.error('Failed to check Odoo config:', error);
      setConfigExists(false);
      setIsOdooActive(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    
    try {
      const response = await syncKpiWithOdoo(kpiId, token);
      setSyncResult({
        success: true,
        message: response.message,
        data: response.data
      });
      
      if (onSyncSuccess) {
        onSyncSuccess(response.data);
      }
    } catch (error) {
      setSyncResult({
        success: false,
        message: error.message || 'Sync failed',
        error: error
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleClose = () => {
    setSyncResult(null);
    setContactsCount(null);
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <SyncOutlined />
          Sync KPI with Odoo CRM
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Checking Odoo configuration...</div>
        </div>
      ) : (
        <div>
          {!configExists && (
            <Alert 
              type="warning" 
              message="Odoo CRM is not configured. Configure it in Settings > General > Odoo CRM to sync." 
              showIcon 
              style={{ marginBottom: 16 }} 
            />
          )}
          
          {!isOdooActive && configExists && (
            <Alert 
              type="warning" 
              message="Odoo CRM is disabled. Enable it in Settings > General > Odoo CRM to sync." 
              showIcon 
              style={{ marginBottom: 16 }} 
            />
          )}
          
          {isOdooActive && (
            <>
              <Alert
                type="info"
                message="This will sync contact data from Odoo CRM to update your KPI progress."
                showIcon
                style={{ marginBottom: 16 }}
              />

              <div style={{ marginBottom: 16 }}>
                <Title level={5}>KPI: {kpiName}</Title>
                
                {contactsCount !== null && (
                  <Statistic
                    title="Available Contacts in Odoo"
                    value={contactsCount}
                    prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                  />
                )}
              </div>

              {syncResult && (
                <Alert
                  type={syncResult.success ? 'success' : 'error'}
                  message={syncResult.success ? 'Sync Successful' : 'Sync Failed'}
                  description={syncResult.message}
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <div style={{ textAlign: 'right' }}>
                <Space>
                  <Button onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    loading={syncing}
                    onClick={handleSync}
                    disabled={!isOdooActive}
                  >
                    {syncing ? 'Syncing...' : 'Sync Now'}
                  </Button>
                </Space>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default OdooSyncModal;